import { schemaRequiresReportType } from "@/config/reportType";
import {
  buildDefaultMappingSelection,
  buildMappingSelectionFromSaved,
  buildSeededRegionReportTypes,
  countSelectedMappingWorksheets,
  normalizeWorksheetPreviewMetadata,
} from "@/lib/mappingRegions";
import type { PipelineFileMeta } from "@/lib/pipelineTypes";
import { getCachedPipelinePreview } from "@/lib/pipelinePreviewCache";
import { classifyFile, loadMappingSelection, previewExtraction } from "@/services/api";
import { resolveBatchSourceFile } from "@/services/batchFileCache";
import type { ParsedWorksheet, TargetSchema } from "@/types";

export interface PipelineFilePreviewContext {
  fileId: string;
  sourceFile: File;
  displayName: string;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  reportType: string | null;
  regionReportTypes: Record<string, string | null>;
  isMultiRegion: boolean;
  schemaId: string;
  schemaName: string;
}

export interface LoadPipelineFilePreviewContextParams {
  fileId: string;
  files: File[];
  file: File | null;
  batchFileMeta: PipelineFileMeta[];
  uploadedDocumentId: string;
  previewFileName: string;
  cachedWorksheets: ParsedWorksheet[];
  cachedMappingSelection: Record<string, boolean>;
  cachedReportType: string | null;
  cachedRegionReportTypes: Record<string, string | null>;
  selectedSchema: string;
  schemas: TargetSchema[];
  onClassifyStart?: () => void;
  onClassifyEnd?: () => void;
  onClassificationResult?: (result: {
    detectedType: string | null;
    allowedReportTypes: string[];
  }) => void;
  onClassifyError?: (error: unknown) => void;
}

export async function loadPipelineFilePreviewContext(
  params: LoadPipelineFilePreviewContextParams
): Promise<PipelineFilePreviewContext> {
  const fileMeta = params.batchFileMeta.find((entry) => entry.fileId === params.fileId);
  const schemaId = fileMeta?.schemaId ?? params.selectedSchema;
  const schemaName = params.schemas.find((s) => s.id === schemaId)?.name ?? schemaId;
  const storedReportType = fileMeta?.reportType ?? null;

  let activeWorksheets = params.cachedWorksheets;
  let activeSelection = params.cachedMappingSelection;
  let activeReportType = params.cachedReportType ?? storedReportType;
  let activeRegionReportTypes = params.cachedRegionReportTypes;

  if (params.uploadedDocumentId !== params.fileId || activeWorksheets.length === 0) {
    const cachedPreview = getCachedPipelinePreview(params.fileId);
    const preview = cachedPreview ?? (await previewExtraction(params.fileId));
    activeWorksheets = preview.worksheets;
    try {
      const savedRegions = await loadMappingSelection(params.fileId);
      activeSelection = buildMappingSelectionFromSaved(
        normalizeWorksheetPreviewMetadata(preview.worksheets),
        savedRegions
      );
    } catch {
      activeSelection = buildDefaultMappingSelection(
        normalizeWorksheetPreviewMetadata(preview.worksheets)
      );
    }

    const mappingCount = countSelectedMappingWorksheets(activeWorksheets, activeSelection);
    if (mappingCount > 1) {
      activeReportType = null;
      activeRegionReportTypes = buildSeededRegionReportTypes(
        activeWorksheets,
        activeSelection,
        storedReportType
      );
    } else {
      activeRegionReportTypes = {};
      if (storedReportType && storedReportType !== "unknown") {
        activeReportType = storedReportType;
      }
    }

    const metaIndex = params.batchFileMeta.findIndex((entry) => entry.fileId === params.fileId);
    const candidateFile =
      metaIndex >= 0
        ? params.files[metaIndex]
        : params.fileId === params.uploadedDocumentId
          ? params.file
          : null;

    if (candidateFile && schemaRequiresReportType(schemaName) && !storedReportType) {
      params.onClassifyStart?.();
      try {
        const result = await classifyFile(candidateFile, schemaId);
        params.onClassificationResult?.({
          detectedType: result.detectedType,
          allowedReportTypes: result.allowedReportTypes,
        });
        activeReportType =
          result.detectedType !== "unknown" ? result.detectedType : activeReportType;
      } catch (err) {
        params.onClassificationResult?.({ detectedType: null, allowedReportTypes: [] });
        params.onClassifyError?.(err);
      } finally {
        params.onClassifyEnd?.();
      }
    } else if (storedReportType) {
      params.onClassificationResult?.({
        detectedType: storedReportType,
        allowedReportTypes:
          fileMeta?.allowedReportTypes && fileMeta.allowedReportTypes.length > 0
            ? fileMeta.allowedReportTypes
            : [storedReportType],
      });
    }
  }

  const sourceFile = await resolveBatchSourceFile(
    params.fileId,
    params.files,
    params.file,
    params.batchFileMeta
  );
  if (!sourceFile) {
    throw new Error(
      "Could not load the original file. Try confirming structure again or re-process the batch."
    );
  }

  const mappingCount = countSelectedMappingWorksheets(activeWorksheets, activeSelection);
  const displayName =
    params.batchFileMeta.find((entry) => entry.fileId === params.fileId)?.fileName ??
    params.previewFileName ??
    sourceFile.name;

  return {
    fileId: params.fileId,
    sourceFile,
    displayName,
    worksheets: activeWorksheets,
    mappingSelection: activeSelection,
    reportType: mappingCount > 1 ? null : activeReportType,
    regionReportTypes: mappingCount > 1 ? activeRegionReportTypes : {},
    isMultiRegion: mappingCount > 1,
    schemaId,
    schemaName,
  };
}
